import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import SourceContext from 'helpers/SourceContext';
import useSource from 'pages/Directory/helpers/useSource';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';
import { transparentize } from 'polished';
import colors from 'styles/colors';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import Dialog from '@material-ui/core/Dialog';
import PageTitle from 'components/Page/PageTitle';
import Breadcrumbs from 'components/Page/Breadcrumbs';
import MappingsLayout from 'components/Mappings/MappingsLayout';

import { AZURE_SOURCE_TYPE_KEY, UPLOAD_CSV_URL } from 'pages/Directory/consts';
import FullScreenTopBar from 'molecules/FullScreenTopBar';
import BeforeYouMap from 'molecules/dataSync/BeforeYouMap';

import { HOME_URL, DIRECTORIES_URL } from 'components/Router/consts';

interface Props {
  shouldDirectToCsvUpload: boolean;
}

function MapAttributes({ shouldDirectToCsvUpload }: Props) {
  const classes = useStyles();
  const history = useHistory();
  const sourceContext = useContext(SourceContext);
  const { sourceInfo } = sourceContext;
  const { directoryId, sourceId, isPrimary, type } = sourceInfo;
  const source = useSource(sourceId);
  const baseUrl = makeSourceCreateBaseUrl(directoryId, sourceId);
  const [mapDialogOpen, setMapDialogOpen] = useState(true);

  useEffect(() => {
    shouldDirectToCsvUpload && history.push(`${baseUrl}${UPLOAD_CSV_URL}`);
  }, []);

  const title = isPrimary ? 'Create a New Directory' : 'Create a Complementary Source';

  const currentStep = type === AZURE_SOURCE_TYPE_KEY ? 3 : 2;
  const totalNumberOfSteps = type === AZURE_SOURCE_TYPE_KEY ? 4 : 3;
  const progress = (currentStep / totalNumberOfSteps) * 100;
  const progressText = `Step ${currentStep} of ${totalNumberOfSteps}`;
  const description = isPrimary
    ? 'A Directory represents a population of people within your organization that can be sourced from a single integration. Let’s get your Directory set up by walking through a few steps.'
    : 'A Complementary Source allows you to append additional data to people within this Directory. Let’s get your Source set up by walking through a few steps.';

  const handleClose = () => {
    history.push(`${HOME_URL}${DIRECTORIES_URL}`);
  };

  const handleMapDialogClose = () => {
    setMapDialogOpen(false);
  };

  return (
    <Dialog fullScreen open>
      <FullScreenTopBar onClose={handleClose} />
      <BeforeYouMap onNext={handleMapDialogClose} open={mapDialogOpen} />
      <div className={classes.root}>
        <Breadcrumbs routes={['Home', { name: 'Directories', title: 'People Data' }]} />
        <PageTitle title={title} subtitle={description} />
        <Box className={classes.progressContainer}>
          <Box className={classes.step}>{progressText}</Box>
          <Box className={classes.progress} data-id="progress-bar">
            <LinearProgress variant="determinate" value={progress} className={classes.bar} />
          </Box>
        </Box>
        <MappingsLayout source={source} />
      </div>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    overflow: 'auto',
    padding: theme.spacing(4),
    flex: '1 1 auto',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  nextButton: {
    minWidth: 86,
    height: 36,
  },
  backButton: {
    width: 67,
    height: 36,
    marginRight: theme.spacing(2),
  },
  footer: {
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(3),
  },
  progressContainer: {
    maxWidth: 1134,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(3),
  },
  progress: {
    maxWidth: 300,
    width: '100%',
  },
  bar: {
    maxWidth: 300,
    backgroundColor: transparentize(0.75, colors.brandColors.tertiaryPalette.moss),
    '& .MuiLinearProgress-bar': {
      backgroundColor: colors.brandColors.tertiaryPalette.moss,
    },
  },
  step: {
    marginRight: theme.spacing(2.375),
    maxWidth: 100,
  },
}));

export default MapAttributes;
